import React, { useState } from "react";
import {
  Spinner,
  Modal,
  Button,
  Table,
  Pagination,
} from "react-bootstrap";
import { BsEye } from "react-icons/bs";
import { useQuery } from "@tanstack/react-query";
import { FiUsers } from "react-icons/fi";

import { InvestorProfileData } from "../Models/InvestorProfileData";
import { useGetInvestors } from "../../api/queries/MuslimInvest/useGetInvestors";
import { useGetMITerms } from "../../api/queries/MuslimInvest/useGetMITerms";
import axiosInstance, { getAPIURL } from "../../api/axiosInstance";
import { Users } from "../../types/Types";
import {
  AdminDetailSection,
  buildInvestorProfileFields,
  buildInvestorTermsFields,
  buildPortalUserFields,
} from "./MuslimInvestAdminDetailSections";
import AdminPanelHeader from "./AdminPanelHeader";

const Investors = () => {
  const [showModal, setShowModal] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const { data, isPending } = useGetInvestors({ pageSize, page });
  const investors = data?.investorProfile || [];

  const totalRecords = data?.totalRecords || 0;
  const totalPages = Math.ceil(totalRecords / pageSize);

  const [selectedInvestor, setSelectedInvestor] =
    useState<InvestorProfileData | null>(null);

  const userIdStr = selectedInvestor?.userId?.toString() ?? "";

  const { data: investorTermsList, isLoading: termsLoading } = useGetMITerms({
    userId: userIdStr,
    enabled: showModal && !!userIdStr,
  });

  const { data: portalUser, isLoading: portalUserLoading } = useQuery({
    queryKey: ["admin-investor-portal-user", userIdStr],
    enabled: showModal && !!userIdStr,
    queryFn: async () => {
      const res = await axiosInstance.get<Users>(
        getAPIURL(`/User/${userIdStr}?id=${userIdStr}`)
      );
      return res.data.user;
    },
  });

  const openModal = (investor: InvestorProfileData) => {
    setSelectedInvestor(investor);
    setShowModal(true);
  };

  const closeModal = () => {
    setSelectedInvestor(null);
    setShowModal(false);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > totalPages) return;
    setPage(newPage);
  };

  return (
    <div className="w-100">
      <AdminPanelHeader
        icon={<FiUsers size={22} />}
        title="Investors"
        description="Manage investor profiles and onboarding."
      />

      {isPending && (
        <div className="d-flex justify-content-center py-5">
          <Spinner animation="border" />
        </div>
      )}

      {!isPending && investors.length === 0 && (
        <div className="alert alert-info text-center">No investors found</div>
      )}

      {!isPending && investors.length > 0 && (
        <>
          <Table
            striped
            bordered
            hover
            responsive
            className="rounded-4 shadow-sm"
          >
            <thead className="table-light">
              <tr>
                <th>ID</th>
                <th>User ID</th>
                <th>Investment Type</th>
                <th>Sectors of Interest</th>
                <th>Stage</th>
                <th>Status</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>

            <tbody>
              {investors.map((inv) => (
                <tr key={inv.id}>
                  <td>{inv.id}</td>
                  <td>{inv.userId ?? "—"}</td>
                  <td>{inv.investmentType || "—"}</td>
                  <td>{inv.sectorsOfInterest || "—"}</td>
                  <td>{inv.developmentStage || "—"}</td>
                  <td>{inv.status || "—"}</td>

                  <td className="d-flex gap-2 justify-content-center">
                    <BsEye
                      size={20}
                      className="text-primary cursor-pointer"
                      onClick={() => openModal(inv)}
                      role="button"
                      aria-label="View investor details"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          <div className="d-flex justify-content-center mt-3">
            <Pagination>
              <Pagination.Prev
                disabled={page === 1}
                onClick={() => handlePageChange(page - 1)}
              />
              {Array.from({ length: totalPages }, (_, i) => (
                <Pagination.Item
                  key={i + 1}
                  active={i + 1 === page}
                  onClick={() => handlePageChange(i + 1)}
                >
                  {i + 1}
                </Pagination.Item>
              ))}
              <Pagination.Next
                disabled={page === totalPages}
                onClick={() => handlePageChange(page + 1)}
              />
            </Pagination>
          </div>
        </>
      )}

      <Modal show={showModal} onHide={closeModal} centered size="xl">
        <Modal.Header closeButton>
          <Modal.Title>Investor details</Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ maxHeight: "75vh", overflowY: "auto" }}>
          {selectedInvestor && (
            <div className="p-2">
              <AdminDetailSection
                title="Portal member"
                fields={buildPortalUserFields(portalUser)}
                loading={portalUserLoading}
              />
              <AdminDetailSection
                title="Investor profile"
                fields={buildInvestorProfileFields(selectedInvestor)}
              />
              {termsLoading && (
                <AdminDetailSection
                  title="Investor terms & compliance"
                  fields={[]}
                  loading
                />
              )}
              {!termsLoading &&
                (investorTermsList?.length ? (
                  investorTermsList.map((terms, index) => (
                    <AdminDetailSection
                      key={terms.id ?? index}
                      title={
                        investorTermsList.length > 1
                          ? `Investor terms & compliance #${index + 1}`
                          : "Investor terms & compliance"
                      }
                      fields={buildInvestorTermsFields(terms)}
                    />
                  ))
                ) : (
                  <AdminDetailSection
                    title="Investor terms & compliance"
                    fields={[
                      {
                        label: "Terms",
                        value: "No investor terms on file",
                      },
                    ]}
                  />
                ))}
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={closeModal}
            className="rounded-pill"
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Investors;
